import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { fromObservable } from '../utils/observable-utils';
import { stringIsNotEmpty } from '../utils/string';

function prettyPrintDateFormat(format: string): string {
    switch (format) {
        case 'yyyy':
            return 'yyyy';
        case 'mmyyyy':
            return 'mm/yyyy';
        case 'mmddyyyy':
            return 'mm/dd/yyyy';
        default:
            return format;
    }
}

/**
 * Whether these components name a day that exists.
 *
 * Built with UTC setters and compared back, because `Date` rolls impossible
 * input over instead of rejecting it: 30 February becomes 1 March, and month 13
 * becomes January of the next year. The result is a real date, so only comparing
 * it against what was asked for reveals the substitution.
 */
function isRealCalendarDate(yyyy: string, mm: string, dd: string): boolean {
    const year = parseInt(yyyy, 10);
    const month = parseInt(mm, 10);
    const day = parseInt(dd, 10);

    if (isNaN(year) || isNaN(month) || isNaN(day)) {
        return false;
    }

    // There is no year zero in this calendar, and it cannot be stored. Every
    // other year is a matter for a field-level min/max rule, not for this.
    if (year < 1) {
        return false;
    }

    const date = new Date(Date.UTC(year, month - 1, day));
    date.setUTCFullYear(year, month - 1, day);

    return (
        date.getUTCFullYear() === year &&
        date.getUTCMonth() === month - 1 &&
        date.getUTCDate() === day
    );
}

// validation function
export function validateDateFactory(format?: string): ValidatorFn {
    return (c: AbstractControl): ValidationErrors | null => {
        // Only a few values are allowed as a format. Since this error message appears on screen, we'll fall back to mmddyyyy since that's the field config default
        const errorMessage = `Please use the ${prettyPrintDateFormat(
            format || 'mmddyyyy'
        )} format.`;
        // The format is right and the date still is not real -- saying "use
        // mm/dd/yyyy" to someone who typed 02/30/2024 in mm/dd/yyyy sends them
        // round in circles.
        const calendarMessage = 'Please enter a date that exists.';
        const ISODateRegExp = () =>
            /^(-?(?:[1-9][0-9]*)?[0-9]{4})-(1[0-2]|0[1-9])-(3[01]|0[1-9]|[12][0-9])$/;
        // sometimes value comes from a @[a.b.c] binding, so it's an observable
        const value = fromObservable(c.value);
        if (!stringIsNotEmpty(value)) {
            return null;
        } else if (!isNaN(value)) {
            return { date: errorMessage };
        } else if (value.indexOf('T') > 0 && ISODateRegExp().test(value.split('T')[0])) {
            //check if ISOString format
            const millis = Date.parse(value);
            if (isNaN(millis)) {
                return { date: errorMessage };
            }

            // Date.parse is not a calendar check. It rejects a 13th month but
            // NORMALISES an impossible day -- "2023-02-29T00:00:00.000Z" parses
            // happily to 1 March -- and the regex above only range-checks 01-31,
            // so both it and Date.parse wave 29 February in a common year
            // through. That is the same substitution this validator exists to
            // refuse, arriving by a different door: as a default value rather
            // than as typing.
            const isoDate = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.split('T')[0]);
            if (isoDate && !isRealCalendarDate(isoDate[1], isoDate[2], isoDate[3])) {
                return { date: calendarMessage };
            }

            return null;
        } else {
            const splitDate: string[] = value.split('/');

            // It's a value from the server
            if (value.indexOf('-') > 0) {
                // Hyphenated values were accepted unconditionally, which let an
                // impossible server-supplied date -- "2024-02-30" -- straight
                // past. Anything YYYY-MM-DD shaped is now held to the calendar;
                // any other hyphenated shape keeps the old pass-through, since
                // this branch is not the place to start guessing at formats.
                const serverDate = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
                if (
                    serverDate &&
                    !isRealCalendarDate(serverDate[1], serverDate[2], serverDate[3])
                ) {
                    return { date: calendarMessage };
                }
                return null;
            }

            switch (format) {
                // Every branch below has to agree with unMaskDateValue, which
                // refuses to build a date out of components that do not name one
                // and returns the raw text instead. A branch that only measures
                // lengths calls that raw text valid, so the form goes green
                // holding "01/1899" where its consumer expects a timestamp --
                // which is a worse failure than the wrong day it replaced.
                // No floor here, because this branch cannot be reached: a bare
                // four-digit year is numeric, so the `!isNaN` guard above returns
                // first, for every possible value of a yyyy field. That is
                // long-standing behaviour and not this change's to alter; the
                // floor for yyyy is enforced in unMaskDateValue instead.
                case 'yyyy':
                    if (splitDate[0]?.length !== 4) {
                        return { date: errorMessage };
                    }
                    return null;
                case 'mmdd':
                    if (splitDate[0]?.length !== 2 || splitDate[1]?.length !== 2) {
                        return { date: errorMessage };
                    }
                    // No year is typed, so unMaskDateValue borrows the current
                    // one; use it here too or the two disagree every leap year
                    // over 02/29.
                    if (
                        !isRealCalendarDate(
                            String(new Date().getUTCFullYear()),
                            splitDate[0],
                            splitDate[1]
                        )
                    ) {
                        return { date: calendarMessage };
                    }
                    return null;
                case 'mmyyyy':
                    if (splitDate[0]?.length !== 2 || splitDate[1]?.length !== 4) {
                        return { date: errorMessage };
                    }
                    // The day is forced to the 1st, so only the month can be wrong.
                    if (!isRealCalendarDate(splitDate[1], splitDate[0], '01')) {
                        return { date: calendarMessage };
                    }
                    return null;
                case 'mmddyyyy':
                default:
                    if (
                        splitDate[0]?.length !== 2 ||
                        splitDate[1]?.length !== 2 ||
                        splitDate[2]?.length !== 4
                    ) {
                        return { date: errorMessage };
                    }
                    // Length is not validity: "02/30/2024" and "13/01/2024" are
                    // both mm/dd/yyyy shaped and neither exists. They used to be
                    // silently corrected to a real day upstream, so this never
                    // saw them; now they arrive intact and must be refused, or
                    // the form accepts a date that is not a date.
                    if (!isRealCalendarDate(splitDate[2], splitDate[0], splitDate[1])) {
                        return { date: calendarMessage };
                    }
                    return null;
            }
        }
    };
}
